/*----------------------------------
- WEBSITE LANDING LINKS
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { Links } from '@/client/pages/landing/types';
import { LANDING_WEBSITE_LINKS_CATALOG } from '@/common/catalogs/landing/publicPages';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';

/*----------------------------------
- TYPES
----------------------------------*/

export type LandingSignupPersona = 'founder' | 'investor';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * No `returnTo` and no `context` here on purpose. `buildConversionAuthHref` now carries
 * both when a click has them, so their absence is a statement: a header, footer or nav
 * link is not a click on anything in particular, and the account screen has to answer
 * with its generic headline rather than promise a destination this link never had.
 */
export const buildLandingPersonaSignupHref = (persona: LandingSignupPersona, from: string): string => {
    return buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        persona,
        /* These hrefs are only ever built by a persona control: the hero pair and the
           footer pair, whose labels are "I'm an Investor" and "I'm a Founder". That click
           is the declaration, so it travels as one and the account screen preselects it
           and asks for confirmation. Without this source the builder drops the persona. */
        personaSource: 'chosen',
        from,
        restart: true,
    });
};

/*----------------------------------
- CATALOG
----------------------------------*/

export const LANDING_WEBSITE_LINKS: Links = {
    ...LANDING_WEBSITE_LINKS_CATALOG,
    productBaseUrl: buildWebsiteAuthHref({ flow: 'cold', mode: 'signup',
        from: 'website_header',
    }),
    productDomains: buildWebsiteAuthHref({ flow: 'cold', mode: 'signup',
        from: 'website_landing',
    }),
    createRadar: buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        from: 'website_create_radar',
    }),
    /*
     * Solutions nav entries land on the persona presentation pages; the persona-tagged
     * signup hrefs below power the CTAs on those pages.
     *
     * They carry `personaSource: 'chosen'` because reaching one of these pages is itself
     * the persona control: the visitor picked "For founders" or "For investors" in the
     * nav and then acted on that page. The account screen still preselects rather than
     * skips, so a visitor who was only browsing can change it in one tap.
     */
    solutionFounderSignup: buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        persona: 'founder',
        personaSource: 'chosen',
        from: 'website_solutions_founders',
        restart: true,
    }),
    solutionInvestorSignup: buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        persona: 'investor',
        personaSource: 'chosen',
        from: 'website_solutions_investors',
        restart: true,
    }),
    solutionStudioSignup: buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        from: 'website_solutions_studios',
        restart: true,
    }),
    signIn: buildWebsiteAuthHref({ flow: 'cold', mode: 'login',
        from: 'website_header',
    }),
};
